import { useUnit } from 'effector-react'
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
} from 'antd'
import { LEVEL_OPTIONS } from 'src/pages/worldCup2026/const'
import { updatePlayerFx } from 'src/pages/worldCup2026/players/model'
import { Player } from 'src/pages/worldCup2026/players/types'

type Props = {
  list: Player[]
  item: Player
  onClose: () => void
}

type FormValues = {
  shirtNumberForm: number
  priceForm: string
  levelForm: 1 | 2 | 3
  isTopForm: boolean
}

export const PlayerEditModal = ({ onClose, list, item }: Props) => {
  const loading = useUnit(updatePlayerFx.pending)
  const { id, fullName, teamID, price, level, isTop, shirtNumber } = item

  const [form] = Form.useForm()
  const { Item } = Form
  const { Text } = Typography

  const initialValues = {
    shirtNumberForm: shirtNumber,
    priceForm: price,
    levelForm: level,
    isTopForm: isTop,
  }
  const valuesForm = Form.useWatch([], form)

  const isSame = () => {
    if (!valuesForm) return false
    const { shirtNumberForm, priceForm, levelForm, isTopForm } = valuesForm
    return (
      String(shirtNumberForm) === String(shirtNumber) &&
      String(priceForm) === String(price) &&
      levelForm === level &&
      isTopForm === isTop
    )
  }

  const handleSubmit = async ({
    shirtNumberForm,
    priceForm,
    levelForm,
    isTopForm,
  }: FormValues) => {
    try {
      await updatePlayerFx({
        body: {
          teamID,
          players: list.map((item) =>
            item.id === id
              ? {
                  ...item,
                  shirtNumber: shirtNumberForm,
                  price: priceForm,
                  level: levelForm,
                  isTop: isTopForm,
                }
              : item,
          ),
        },
      })
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Modal
      title={
        <div style={{ fontSize: 20 }}>
          Edit{' '}
          <strong style={{ color: 'var(--color-primary)' }}>{fullName}</strong>{' '}
          Player Settings
        </div>
      }
      centered
      wrapClassName="ant_modal"
      width={500}
      maskClosable={false}
      open={Boolean(item)}
      onCancel={onClose}
      footer={null}
    >
      <Divider />
      <Form onFinish={handleSubmit} initialValues={initialValues} form={form}>
        <Item
          required={false}
          name="shirtNumberForm"
          style={{ width: 250 }}
          label={
            <Text style={{ fontSize: 16 }} strong>
              Shirt Number
            </Text>
          }
          rules={[
            {
              required: true,
              transform: (value) => value?.trim(),
              message: 'Required field',
            },
            {
              pattern: /^[1-9]\d*$/,
              message: 'Shirt Number must be a positive number',
            },
          ]}
        >
          <Input />
        </Item>
        <Item
          required={false}
          style={{ width: 150 }}
          name="priceForm"
          label={
            <Text style={{ fontSize: 16 }} strong>
              Price
            </Text>
          }
          rules={[
            {
              required: true,
              transform: (value) => value?.trim(),
              message: 'Required field',
            },
            {
              pattern: /^\d+(?:[.,]\d+)?$/,
              message: 'Price must be a positive number',
            },
          ]}
        >
          <Input />
        </Item>
        <Item
          required={false}
          name="levelForm"
          style={{ width: 150 }}
          label={
            <Text style={{ fontSize: 16 }} strong>
              Level
            </Text>
          }
        >
          <Select options={LEVEL_OPTIONS} />
        </Item>
        <Item required={false} valuePropName="checked" name="isTopForm">
          <Checkbox style={{ fontSize: 16 }}>
            <strong>is Top Player</strong>
          </Checkbox>
        </Item>
        <Divider />
        <Flex gap={8} justify="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={isSame() || loading}
          >
            Save
          </Button>
        </Flex>
      </Form>
    </Modal>
  )
}
